import { createInstance, publish, subscribe, addListener, getChannelOccupancy } from "./pubnub";

class DomestikaWebSocket {
  /**
   * It initializes the WebSocket, returning a Promise with its instance
   * @param {Object} config Config object to create WebSocket instance
   * @param {String} config.uuid Unique user session's identifier on WebSocket
   * @param {String} config.authKey Key to communicate with private channels in the WebSocket
   * @param {String} config.publishKey Key to be able to publish in WebSocket
   * @param {String} config.subscribeKey Key to be able to subscribe to WebSocket's channels
   * @return {Promise} Promise with a WebSocket instance
   */
  async init({ uuid, authKey, publishKey, subscribeKey } = {}) {
    if (!this.instance) {
      if (!uuid) {
        throw new Error("The uuid is required to create a WebSocket instance.");
      }

      if (!authKey) {
        throw new Error("The authKey is required to create a WebSocket instance.");
      }

      if (!publishKey) {
        throw new Error("The publishKey is required to create a WebSocket instance.");
      }

      if (!subscribeKey) {
        throw new Error("The subscribeKey is required to create a WebSocket instance.");
      }

      this.instance = await createInstance({ uuid, authKey, publishKey, subscribeKey });
    }

    return this.instance;
  }

  /**
   * It sends a message, given a channel name and a content
   * @param {Object} config Object to send a WebSocket message
   * @param {String} config.channel Name of the channel to send the message to
   * @param {any} config.message Content to be sent as message
   * @param {Object} config.options Object containing optional attributes for the publish
   */
  async send({ channel, message, options = {} } = {}) {
    if (!this.instance) {
      throw new Error("You need to create a WebSocket instance before sending messages.");
    }

    if (!channel || !message) {
      throw new Error("Channel and message are required for sending messages.");
    }

    return publish({ pubnubInstance: this.instance, channel, message, options });
  }

  /**
   * It subscribes to channels, so that listeners can be registered and triggered for the subscribed channels communication
   * @param {Object} config Object to subscribe to WebSocket channels
   * @param {Array} config.channels List of channels to subscribe to
   * @param {Boolean} config.withPresence Automatically subscribe to presence channel
   */
  subscribe({ channels, withPresence = false } = {}) {
    if (!this.instance) {
      throw new Error("You need to create a WebSocket instance before subscribing to channels.");
    }

    if (!Array.isArray(channels)) {
      throw new Error("Channels are required for subscribing to it.");
    }

    subscribe({ pubnubInstance: this.instance, channels, withPresence });
  }

  /**
   * It registers a listener for incoming messages on the subscribed channels
   * @param {Object} config Object to add a listener to WebSocket messages
   * @param {Object} config.listener Object with type of listener to register
   */
  listen({ listener } = {}) {
    if (!this.instance) {
      throw new Error("You need to create a WebSocket instance before listening to messages.");
    }

    if (typeof listener !== "object") {
      throw new Error("Listener is required for registering it.");
    }

    addListener({ pubnubInstance: this.instance, listener });
  }

  /**
   * It gets the current occupancy of a channel
   * @param {Object} config Object to add a listener to Pubnub messages
   * @param {String} config.channelName Name of the channel to check occupancy
   */
  async getChannelOccupancy({ channelName } = {}) {
    if (!this.instance) {
      throw new Error("You need to create a WebSocket instance before checking occupancy.");
    }

    if (!channelName?.length) {
      throw new Error("Channel name is required for checking occupancy.");
    }

    return getChannelOccupancy({ pubnubInstance: this.instance, channelName });
  }
}

Domestika.webSocket = new DomestikaWebSocket();
